import { z } from 'zod';
import { count, formatEnum, isoDate, isoTimestamp, nokAmount, ratio, segmentEnum, sourceEnum, } from './primitives.js';
/** One computed day in the blended "all" view (mirrors api ComputedMetrics). */
export const computedMetricsSchema = z.object({
    date: isoDate,
    adSpendAed: nokAmount,
    adSpend: nokAmount,
    impressions: count,
    reach: count,
    cpm: ratio,
    clicks: count,
    ctr: ratio,
    cpc: ratio,
    bookings: count,
    lpPct: ratio,
    cpl: ratio,
    costPerBooking: ratio,
    showedUp: count,
    surPct: ratio,
    surCost: ratio,
    soldMemberships: count,
    newMembers: count,
    recurringMembers: count,
    ccPct: ratio,
    lcPct: ratio,
    revenue: nokAmount,
    newRevenue: nokAmount,
    recurringRevenue: nokAmount,
    cash: nokAmount,
    cpa: ratio,
    rPl: nokAmount,
    cPl: nokAmount,
    rRoi: ratio,
    cRoi: ratio,
});
/** One Meta-only day for the lead/booking segment views (mirrors api MetaRow). */
export const metaRowSchema = z.object({
    date: isoDate,
    adSpend: nokAmount,
    impressions: count,
    reach: count,
    cpm: ratio,
    clicks: count,
    ctr: ratio,
    cpc: ratio,
    leads: count,
    cpl: ratio,
    bookings: count,
    costPerBooking: ratio,
    lpPct: ratio,
});
/** The self-describing metric registry entry (drives columns, formatters, color). */
export const metricDefSchema = z.object({
    key: z.string(),
    label: z.string(),
    format: formatEnum,
    source: sourceEnum,
    higherIsBetter: z.boolean().nullable(),
    decision: z.boolean(),
    desc: z.string(),
    segments: z.array(segmentEnum),
});
export const targetSchema = z.object({
    comparator: z.string(),
    targetValue: z.number(),
    notes: z.string().nullable(),
    active: z.boolean(),
});
export const targetsMapSchema = z.record(z.string(), targetSchema);
export const ruleSchema = z.object({
    id: z.number(),
    name: z.string(),
    metricKey: z.string(),
    comparator: z.string(),
    threshold: z.number(),
    windowDays: z.number(),
    severity: z.string(),
    message: z.string(),
    recommendation: z.string().nullable(),
    enabled: z.boolean(),
    createdAt: isoTimestamp,
    updatedAt: isoTimestamp,
});
export const alertSchema = z.object({
    id: z.number(),
    date: isoDate,
    ruleName: z.string(),
    metricKey: z.string(),
    severity: z.string(),
    observedValue: z.number().nullable(),
    threshold: z.number().nullable(),
    message: z.string(),
    recommendation: z.string().nullable(),
    status: z.string(),
});
export const segmentClassificationSchema = z.object({
    lead: z.array(z.string()),
    booking: z.array(z.string()),
    other: z.array(z.string()),
    reconDeltaNok: z.number(),
});
export const funnelStageSchema = z.object({
    key: z.string(),
    label: z.string(),
    value: z.number(),
    ofPrev: ratio,
    attributed: z.string().optional(),
    hardStop: z.boolean().optional(),
});
export const dashboardUserSchema = z.object({
    email: z.string(),
    name: z.string().optional(),
    picture: z.string().optional(),
});
// ---- Landing playbook shapes (the /landing endpoint) ----------------------
export const experimentSchema = z.object({
    id: z.string(),
    title: z.string(),
    hypothesis: z.string(),
    evidence: z.array(z.string()),
    impact: z.number(),
    confidence: z.number(),
    ease: z.number(),
    status: z.enum(['idea', 'running', 'done', 'win', 'loss']),
    metric: z.enum(['lpPct', 'ctr', 'bookEventRate', 'costPerBooking']),
    where: z.enum(['theme', 'backend', 'dashboard']),
    links: z.object({ section: z.string().optional(), asset: z.string().optional() }).optional(),
    result: z
        .object({ before: z.number(), after: z.number(), deltaPct: z.number(), note: z.string() })
        .nullable()
        .optional(),
    ice: z.number(),
});
export const intakeNoteSchema = z.object({
    id: z.string(),
    date: z.string(),
    text: z.string(),
    tag: z.string().optional(),
});
export const shippedChangeSchema = z.object({
    id: z.string(),
    area: z.string(),
    title: z.string(),
    before: z.string(),
    after: z.string(),
    why: z.string(),
    evidence: z.string().optional(),
    example: z.string().optional(),
    where: z.string(),
    status: z.enum(['live', 'preview', 'planned']),
    reviewCaught: z.boolean().optional(),
});
//# sourceMappingURL=domain.js.map